import { resolveBatteryDisplayPercent, type BatteryLike } from '@/shared/helper/battery-status';
import { isRenderableLatLon } from '@/shared/geo/coordinates';
import { WsEventType, WsMessageType, type WsBaseMessage, type WsDeviceCountMessage } from '@/shared/realtime/ws-types';
import type { DeviceIconConfig } from '@/types/device-icon';
import { z } from 'zod';
import { workerMobileProjectionSchema } from '@/domains/worker-mobile/model';

import { resolveSaconGroupPresentationStatus } from './sacon-state-presentation';
import { normalizeMarkerInstallLocation } from './vworld-marker-title';

export type DeviceStatusLevel = 'normal' | 'warning' | 'caution' | 'offline' | 'unknown' | 'unregistered';
export type DeviceMarkerKafkaEventLevel = 'info' | 'warning' | 'alert' | 'error';

export const deviceGroupMeasurementsSchema = z.object({
  co: z.number().nullable().optional(),
  o2: z.number().nullable().optional(),
  h2s: z.number().nullable().optional(),
  exp: z.number().nullable().optional(),
  tvoc: z.number().nullable().optional(),
  co2: z.number().nullable().optional(),
  tp: z.number().nullable().optional(),
  appr_tp: z.number().nullable().optional(),
  hm: z.number().nullable().optional(),
  pm1_0: z.number().nullable().optional(),
  pm2_5: z.number().nullable().optional(),
  pm10: z.number().nullable().optional(),
  dba: z.number().nullable().optional(),
  dbv: z.number().nullable().optional(),
  wind_spd: z.number().nullable().optional(),
  wind_deg: z.number().nullable().optional(),
  wind_dir: z.string().nullable().optional(),
  xdeg: z.number().nullable().optional(),
  ydeg: z.number().nullable().optional(),
  zdeg: z.number().nullable().optional(),
  power_operation_state: z.number().int().min(0).max(15).nullable().optional(),
  power_operation_state_name: z.string().nullable().optional(),
  power_charging_state: z.number().int().min(0).max(15).nullable().optional(),
  power_charging_state_name: z.string().nullable().optional(),
  router_power: z.number().int().min(0).max(15).nullable().optional(),
  router_timer_control: z.number().int().min(0).max(15).nullable().optional(),
  gps_on_off: z.number().int().min(0).max(15).nullable().optional(),
  gps_timer_control: z.number().int().min(0).max(15).nullable().optional(),
  indicator_led: z.number().int().min(0).max(15).nullable().optional(),
  indicator_timer_control: z.number().int().min(0).max(15).nullable().optional(),
  power_off_reason: z.number().int().min(0).max(255).nullable().optional(),
  power_off_reason_name: z.string().nullable().optional(),
});

export const deviceMarkerKafkaEventSchema = z.object({
  active: z.boolean(),
  at: z.string(),
  eventKey: z.string(),
  event: z.string(),
  label: z.string(),
  level: z.enum(['info', 'warning', 'alert', 'error']),
  message: z.string().optional(),
  autoClearAfterMs: z.number().int().positive().optional(),
  shouldAutoFocusMap: z.boolean().optional(),
  shouldAutoOpenMarkerPopup: z.boolean().optional(),
  sourceDeviceUid: z.string().optional(),
  displayDeviceUid: z.string().optional(),
  alarmUid: z.string().optional(),
  alarmVersion: z.number().int().nonnegative().optional(),
  sourceSensorVersion: z.number().int().nonnegative().optional(),
  eventType: z.string().optional(),
  eventAt: z.string().optional(),
  identities: z.array(z.string()).optional(),
  tooltip: z.string().optional(),
  topic: z.string().optional(),
});

const optionalCoordinateSchema = z.preprocess(value => (value === null ? undefined : value), z.number().optional());

export const deviceStatusSummarySchema = z.object({
  field: z.number(),
  device: z.number(),
  worker: z.number(),
  alarm: z.number(),
});

export const deviceStatusItemSchema = z.object({
  id: z.string(),
  entityUid: z.string().optional(),
  // V2 제품 마커를 기존 업무 목록의 장비·구성 센서 식별자와 연결합니다.
  navigationUids: z.array(z.string()).optional(),
  name: z.string(),
  mobileDevice: workerMobileProjectionSchema.optional(),
  mobilePeers: z.array(workerMobileProjectionSchema).optional(),
  deviceName: z.string().optional(),
  deviceType: z.string().optional(),
  workerName: z.string().optional(),
  // 원본 센서별 지급 정보와 해제 진행 상태를 팝업에 전달합니다.
  sourceRecipients: z.record(z.string(), z.unknown()).optional(),
  warningClearAllowed: z.boolean().optional(),
  warningClearPendingKey: z.string().optional(),
  deviceCode: z.string().optional(),
  productUid: z.string().optional(),
  productName: z.string().optional(),
  productLoraType: z.number().int().optional(),
  productSensorType: z.number().int().optional(),
  productSensorTypeName: z.string().optional(),
  productSensorTypeLabel: z.string().optional(),
  isCctv: z.boolean().optional(),
  cameraUid: z.string().optional(),
  cameraStatus: z.string().optional(),
  cameraIsOn: z.boolean().optional(),
  cameraStreamHealthy: z.boolean().optional(),
  cameraEffectiveIsOn: z.boolean().optional(),
  mainStream: z.string().optional(),
  subStream: z.string().optional(),
  rId: z.string().optional(),
  sId: z.string().optional(),
  saconState: z.string().optional(),
  rawData: z.string().optional(),
  gpsValid: z.boolean().optional(),
  gpsStatus: z.string().optional(),
  status: z.enum(['normal', 'warning', 'caution', 'offline', 'unknown', 'unregistered']),
  lastSeen: z.string(),
  lastSeenAt: z.string().optional(),
  location: z.string(),
  hasInstallLocationText: z.boolean().optional(),
  fieldUid: z.string().optional(),
  lat: optionalCoordinateSchema,
  lon: optionalCoordinateSchema,
  markerIconUrl: z.string().optional(),
  deviceIcon: z.unknown().optional(),
  mapMarkerIcon: z.unknown().optional(),
  constructionEquipment: z.unknown().optional(),
  isConstructionEquipment: z.boolean().optional(),
  isEscapePreventionEnabled: z.boolean().optional(),
  batteryPercent: z.number().min(0).max(100).nullable().optional(),
  batteryVoltage: z.number().finite().positive().optional(),
  batteryStatus: z.enum(['unknown', 'low', 'normal', 'good', 'full']).optional(),
  deviceStateRaw: z.enum(['NORMAL', 'ALERT', 'WARNING', 'OFF', 'INVISUAL']).optional(),
  geofenceIsInside: z.boolean().nullable().optional(),
  escapeAlertActive: z.boolean().optional(),
  movementStatus: z.string().optional(),
  lastMovementAt: z.string().nullable().optional(),
  sensorState: z.string().optional(),
  sensorStateName: z.string().optional(),
  sensorCount: z.number().int().nonnegative().optional(),
  powerOperationState: z.number().int().min(0).max(15).optional(),
  powerOperationStateName: z.string().optional(),
  powerChargingState: z.number().int().min(0).max(15).optional(),
  powerChargingStateName: z.string().optional(),
  routerPower: z.number().int().min(0).max(15).optional(),
  routerTimerControl: z.number().int().min(0).max(15).optional(),
  gpsOnOff: z.number().int().min(0).max(15).optional(),
  gpsTimerControl: z.number().int().min(0).max(15).optional(),
  indicatorLed: z.number().int().min(0).max(15).optional(),
  indicatorTimerControl: z.number().int().min(0).max(15).optional(),
  powerOffReason: z.number().int().min(0).max(255).optional(),
  powerOffReasonName: z.string().optional(),
  connectionState: z.string().optional(),
  movementEvent: z.string().optional(),
  movementEventLabel: z.string().optional(),
  movementOffReason: z.string().optional(),
  movementOffReasonName: z.string().optional(),
  movementDisplayLabel: z.string().optional(),
  measurements: deviceGroupMeasurementsSchema.optional(),
  deviceUpdatedAt: z.string().optional(),
  deviceGroupUid: z.string().optional(),
  mainDeviceUid: z.string().optional(),
  displayDeviceUid: z.string().optional(),
  displayDeviceName: z.string().optional(),
  displayDeviceCode: z.string().optional(),
  displayRId: z.string().optional(),
  displaySId: z.string().optional(),
  displayProductUid: z.string().optional(),
  displayProductSensorType: z.number().int().optional(),
  sourceDeviceUid: z.string().optional(),
  sourceDeviceName: z.string().optional(),
  sourceDeviceCode: z.string().optional(),
  sourceRId: z.string().optional(),
  sourceSId: z.string().optional(),
  sourceProductUid: z.string().optional(),
  sourceProductSensorType: z.number().int().optional(),
  originalDeviceUid: z.string().optional(),
  originalDeviceName: z.string().optional(),
  originalDeviceCode: z.string().optional(),
  originalRId: z.string().optional(),
  originalSId: z.string().optional(),
  originalProductUid: z.string().optional(),
  originalProductSensorType: z.number().int().optional(),
  groupName: z.string().optional(),
  groupDeviceCount: z.number().int().nonnegative().optional(),
  groupTotalDeviceCount: z.number().int().positive().optional(),
  groupChildCounterCount: z.number().int().nonnegative().optional(),
  isGroupMainDevice: z.boolean().optional(),
  isGroupCounterParent: z.boolean().optional(),
  isGroupCounterChild: z.boolean().optional(),
  sourceIsGroupCounterChild: z.boolean().optional(),
  usesGroupData: z.boolean().optional(),
  kafkaEvent: deviceMarkerKafkaEventSchema.optional(),
});

export const deviceStatusSnapshotSchema = z.object({
  summary: deviceStatusSummarySchema,
  items: z.array(deviceStatusItemSchema),
  source: z.literal('sse'),
  updatedAt: z.string(),
});

/** 지도와 Tunnel 마커 popup의 운용 상태 라벨을 반환합니다. */
export function formatMapMarkerStatus(status: DeviceStatusItem['status']): string {
  const labels: Record<DeviceStatusItem['status'], string> = {
    caution: '주의',
    normal: 'ON',
    offline: 'OFF',
    unknown: '상태 미확인',
    unregistered: '미등록',
    warning: '경고',
  };
  return labels[status];
}

export type DeviceStatusSummary = z.infer<typeof deviceStatusSummarySchema>;
export type DeviceGroupMeasurements = z.infer<typeof deviceGroupMeasurementsSchema>;
export type DeviceMarkerKafkaEvent = z.infer<typeof deviceMarkerKafkaEventSchema>;
export type DeviceStatusItem = z.infer<typeof deviceStatusItemSchema>;
export type DeviceStatusSnapshot = z.infer<typeof deviceStatusSnapshotSchema>;
export type DeviceStatusItemWithIcon = DeviceStatusItem & {
  deviceIcon?: DeviceIconConfig | null;
  mapMarkerIcon?: DeviceIconConfig | null;
  markerIconUrl?: string;
};

export const DEFAULT_CONSTRUCTION_EQUIPMENT_MARKER_ICON: DeviceIconConfig = {
  provider: 'system',
  name: 'marker',
  marker_size: 72,
  size: 20,
};

/**
 * 건설장비 메타 객체를 안전하게 읽습니다.
 */
function readConstructionEquipmentRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

/**
 * 장비 아이콘 설정으로 사용할 수 있는 객체인지 확인합니다.
 */
function readDeviceIconConfig(value: unknown): DeviceIconConfig | null {
  const record = readConstructionEquipmentRecord(value);
  if (!record) {
    return null;
  }

  const provider = readString(record.provider);
  const name = readString(record.name);
  if (!provider || !name || (provider !== 'material' && provider !== 'lucide' && provider !== 'system')) {
    return null;
  }

  return value as DeviceIconConfig;
}

/**
 * 지도 마커 표시명을 건설장비명 우선으로 계산합니다.
 */
export function resolveConstructionEquipmentMarkerName(constructionEquipment: unknown, fallbackName: string): string {
  const equipmentRecord = readConstructionEquipmentRecord(constructionEquipment);
  if (!equipmentRecord) {
    return fallbackName;
  }

  return (
    readString(equipmentRecord.equipment_name) ??
    readString(equipmentRecord.equipmentName) ??
    readString(equipmentRecord.equipment_code) ??
    readString(equipmentRecord.equipmentCode) ??
    fallbackName
  );
}

/**
 * 지도 마커 아이콘을 건설장비 아이콘, 장비 아이콘, 기본 건설장비 마커 순서로 계산합니다.
 */
export function resolveConstructionEquipmentMarkerIcon(constructionEquipment: unknown, mapMarkerIcon?: unknown, deviceIcon?: unknown): DeviceIconConfig | undefined {
  const fallbackMapMarkerIcon = readDeviceIconConfig(mapMarkerIcon);
  const fallbackDeviceIcon = readDeviceIconConfig(deviceIcon);
  const equipmentRecord = readConstructionEquipmentRecord(constructionEquipment);
  if (!equipmentRecord) {
    return fallbackMapMarkerIcon ?? fallbackDeviceIcon ?? undefined;
  }

  return (
    readDeviceIconConfig(equipmentRecord.equipment_icon ?? equipmentRecord.equipmentIcon) ??
    fallbackMapMarkerIcon ??
    fallbackDeviceIcon ??
    DEFAULT_CONSTRUCTION_EQUIPMENT_MARKER_ICON
  );
}

/**
 * GPS 수신 실패 상태 코드인지 확인합니다.
 */
export function isGpsNoFixStatus(value: string | null | undefined): boolean {
  return (
    String(value ?? '')
      .trim()
      .toUpperCase() === 'NO_FIX'
  );
}

/**
 * 장비 상태 아이템이 GPS 미수신 이벤트를 담고 있는지 확인합니다.
 */
export function isGpsNoFixItem(item: Pick<DeviceStatusItem, 'saconState' | 'gpsValid' | 'gpsStatus'>): boolean {
  const state = String(item.saconState ?? '')
    .trim()
    .toUpperCase();
  return state === 'GPS' && (item.gpsValid === false || isGpsNoFixStatus(item.gpsStatus));
}

/**
 * 지도 팝업과 패널에 표시할 GPS 미수신 문구를 반환합니다.
 */
export function formatGpsNoFixLabel(item: Pick<DeviceStatusItem, 'saconState' | 'gpsValid' | 'gpsStatus'>): string | null {
  if (!isGpsNoFixItem(item)) {
    return null;
  }

  return `GPS 수신 실패 / ${String(item.gpsStatus || 'NO_FIX')
    .trim()
    .toUpperCase()}`;
}

const DEVICE_STATUS_EVENT_TYPES = new Set<WsEventType>([
  WsEventType.SACON_PRODUCT_PROCESSED,
  WsEventType.SACON_PRODUCT_INFO,
  WsEventType.SACON_PRODUCT_ALERT,
  WsEventType.SACON_PRODUCT_WARNING,
  WsEventType.DEVICE_GEOFENCE_ESCAPE,
  WsEventType.DEVICE_GEOFENCE_RETURNED,
  WsEventType.DEVICE_COUNT,
]);

/**
 * 장비 현황 위젯의 기본 빈 스냅샷을 생성합니다.
 */
export function createEmptyDeviceStatusSnapshot(): DeviceStatusSnapshot {
  return {
    summary: {
      field: 0,
      device: 0,
      worker: 0,
      alarm: 0,
    },
    items: [],
    source: 'sse',
    updatedAt: new Date().toISOString(),
  };
}

/**
 * 외부 소비자가 안전하게 사용할 수 있도록 스냅샷을 복제합니다.
 */
export function cloneDeviceStatusSnapshot(snapshot: DeviceStatusSnapshot): DeviceStatusSnapshot {
  return {
    ...snapshot,
    summary: { ...snapshot.summary },
    items: snapshot.items.map(item => ({ ...item })),
  };
}

/**
 * 장비 현황 스냅샷에 반영할 대상 메시지인지 판별합니다.
 */
export function isDeviceStatusMessage(message: WsBaseMessage): boolean {
  return (message.type === WsMessageType.DEVICE || message.type === WsMessageType.AIS) && DEVICE_STATUS_EVENT_TYPES.has(message.event);
}

/**
 * 루트 또는 body에 포함된 field UID를 문자열로 정규화합니다.
 */
export function resolveMessageFieldUid(message: WsBaseMessage): string | null {
  const rootFieldUid = readString((message as WsBaseMessage & { field_uid?: unknown }).field_uid) ?? readString((message as WsBaseMessage & { fieldUid?: unknown }).fieldUid);
  if (rootFieldUid) {
    return rootFieldUid;
  }

  const body = (message.body ?? {}) as Record<string, unknown>;
  return readString(body.field_uid) ?? readString(body.fieldUid);
}

function nowIso() {
  return new Date().toISOString();
}

function formatLastSeen(timestamp?: string): string {
  const parsed = timestamp ? new Date(timestamp) : new Date();

  if (Number.isNaN(parsed.getTime())) {
    return new Date().toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul', hour12: false });
  }

  return parsed.toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul', hour12: false });
}

/**
 * 비교와 전달에 사용할 수 있도록 수신 시각을 ISO 문자열로 정규화합니다.
 */
function normalizeLastSeenTimestamp(timestamp?: string): string | undefined {
  if (!timestamp) {
    return undefined;
  }

  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return parsed.toISOString();
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * 상태 코드성 문자열을 대문자 코드로 정규화합니다.
 */
function readStatusCode(value: unknown): string | null {
  return readString(value)?.toUpperCase() ?? null;
}

function readNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      return numericValue;
    }
  }

  return null;
}

/**
 * 숫자형 측정값을 null 보존 형태로 읽습니다.
 */
function readNullableNumber(value: unknown): number | null | undefined {
  if (value === null) {
    return null;
  }

  const numericValue = readNumber(value);
  return numericValue === null ? undefined : numericValue;
}

/**
 * 실시간 payload의 초기값성 0%/0V 배터리를 정보없음으로 정규화합니다.
 */
function readBatteryPercent(body: Record<string, unknown>): number | undefined {
  const batt = (body.batt && typeof body.batt === 'object' ? body.batt : {}) as Record<string, unknown>;
  const battery: BatteryLike = {
    volt: batt.volt as BatteryLike['volt'],
    percent: (body.batt_percent ?? batt.percent) as BatteryLike['percent'],
    level: batt.level as BatteryLike['level'],
    state: batt.state as BatteryLike['state'],
    status: (body.batt_status ?? batt.status) as BatteryLike['status'],
    name: batt.name as BatteryLike['name'],
    isLowBattery: body.isLowBattery as boolean | null | undefined,
    is_low_battery: body.is_low_battery as boolean | null | undefined,
  };

  return resolveBatteryDisplayPercent(battery);
}

/**
 * 불리언 형태의 입력값을 안전하게 해석합니다.
 */
function readBoolean(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    if (normalizedValue === 'true') {
      return true;
    }
    if (normalizedValue === 'false') {
      return false;
    }
  }

  return null;
}

function readRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function readGroupData(body: Record<string, unknown>): Record<string, unknown> | null {
  return readRecord(body.group_data) ?? readRecord(body.groupData);
}

/**
 * 연결 장비 패널에서 재사용할 제품별 측정값을 WebSocket body에서 추출합니다.
 */
function readMeasurements(body: Record<string, unknown>): DeviceGroupMeasurements | undefined {
  const measurements: DeviceGroupMeasurements = {
    co: readNullableNumber(body.co),
    o2: readNullableNumber(body.o2),
    h2s: readNullableNumber(body.h2s),
    exp: readNullableNumber(body.exp),
    tvoc: readNullableNumber(body.tvoc),
    co2: readNullableNumber(body.co2),
    tp: readNullableNumber(body.tp),
    appr_tp: readNullableNumber(body.appr_tp ?? body.apprTp),
    hm: readNullableNumber(body.hm),
    pm1_0: readNullableNumber(body.pm1_0 ?? body.pm1),
    pm2_5: readNullableNumber(body.pm2_5 ?? body.pm25),
    pm10: readNullableNumber(body.pm10),
    dba: readNullableNumber(body.dba),
    dbv: readNullableNumber(body.dbv),
    wind_spd: readNullableNumber(body.wind_spd ?? body.windSpd),
    wind_deg: readNullableNumber(body.wind_deg ?? body.windDeg),
    wind_dir: readString(body.wind_dir) ?? readString(body.windDir),
    power_operation_state: readNullableNumber(body.power_operation_state ?? body.powerOperationState),
    power_operation_state_name: readString(body.power_operation_state_name) ?? readString(body.powerOperationStateName),
    power_charging_state: readNullableNumber(body.power_charging_state ?? body.powerChargingState),
    power_charging_state_name: readString(body.power_charging_state_name) ?? readString(body.powerChargingStateName),
    router_power: readNullableNumber(body.router_power ?? body.routerPower),
    router_timer_control: readNullableNumber(body.router_timer_control ?? body.routerTimerControl),
    gps_on_off: readNullableNumber(body.gps_on_off ?? body.gpsOnOff),
    gps_timer_control: readNullableNumber(body.gps_timer_control ?? body.gpsTimerControl),
    indicator_led: readNullableNumber(body.indicator_led ?? body.indicatorLed),
    indicator_timer_control: readNullableNumber(body.indicator_timer_control ?? body.indicatorTimerControl),
    power_off_reason: readNullableNumber(body.power_off_reason ?? body.powerOffReason),
    power_off_reason_name: readString(body.power_off_reason_name) ?? readString(body.powerOffReasonName),
  };

  const hasMeasurement = Object.values(measurements).some(value => value !== undefined && value !== null);
  return hasMeasurement ? measurements : undefined;
}

/**
 * 그룹 집계에 OFF가 아닌 구성원이 남아 있는지 확인합니다.
 */
function hasActiveGroupMember(body: Record<string, unknown>): boolean {
  const groupData = readGroupData(body);
  const activeDeviceCount = readNumber(groupData?.active_device_count) ?? readNumber(groupData?.activeDeviceCount);
  return activeDeviceCount !== null && activeDeviceCount > 0;
}

/**
 * 그룹 집계가 전체 OFF 상태인지 확인합니다.
 */
function isOfflineGroupData(groupData: Record<string, unknown> | null): boolean {
  if (!groupData) {
    return false;
  }

  const deviceCount = readNumber(groupData.device_count) ?? readNumber(groupData.deviceCount);
  const activeDeviceCount = readNumber(groupData.active_device_count) ?? readNumber(groupData.activeDeviceCount);
  return deviceCount !== null && deviceCount > 0 && activeDeviceCount !== null && activeDeviceCount <= 0;
}

/**
 * 카운터 부모/자식 OFF 이벤트는 그룹 전체 OFF 집계로 부모 마커 상태를 덮지 않습니다.
 */
function shouldUseSaconGroupDataState(body: Record<string, unknown>, groupData: Record<string, unknown> | null): boolean {
  if (!groupData) {
    return false;
  }

  if (!isOfflineGroupData(groupData)) {
    return true;
  }

  const isGroupCounterParent = readBoolean(body.is_group_counter_parent) ?? readBoolean(body.isGroupCounterParent);
  const isSourceGroupCounterChild = readBoolean(body.source_is_group_counter_child) ?? readBoolean(body.sourceIsGroupCounterChild);
  return isGroupCounterParent !== true && isSourceGroupCounterChild !== true;
}

/**
 * 그룹 대표 장비 자체의 OFF 상태가 그룹 집계 상태보다 우선인지 확인합니다.
 */
function shouldPreferExplicitDeviceState(body: Record<string, unknown>, explicitDeviceState: 'NORMAL' | 'ALERT' | 'WARNING' | 'OFF' | 'INVISUAL' | null): boolean {
  if (explicitDeviceState !== 'OFF') {
    return false;
  }

  if (hasActiveGroupMember(body)) {
    return false;
  }

  const mainDeviceUid = readString(body.main_device_uid) ?? readString(body.mainDeviceUid);
  if (!mainDeviceUid) {
    return true;
  }

  const deviceUid = readString(body.device_uid) ?? readString(body.deviceUid) ?? readString(body.display_device_uid) ?? readString(body.displayDeviceUid) ?? readString(body.uid);
  return readBoolean(body.is_group_main_device) === true || (deviceUid !== null && deviceUid === mainDeviceUid);
}

/**
 * 백엔드 장비 상태 문자열을 표준 enum 이름으로 정규화합니다.
 */
function normalizeDeviceStateRaw(value: unknown): 'NORMAL' | 'ALERT' | 'WARNING' | 'OFF' | 'INVISUAL' | null {
  const normalizedValue = readString(value)?.toUpperCase();
  if (!normalizedValue) {
    return null;
  }

  if (normalizedValue === 'NORMAL' || normalizedValue === 'ALERT' || normalizedValue === 'WARNING' || normalizedValue === 'OFF' || normalizedValue === 'INVISUAL') {
    return normalizedValue;
  }

  return null;
}

/**
 * 명시적 장비 상태 값을 위젯 상태 값으로 변환합니다.
 */
function mapDeviceStateRawToStatus(deviceStateRaw: 'NORMAL' | 'ALERT' | 'WARNING' | 'OFF'): DeviceStatusLevel {
  if (deviceStateRaw === 'ALERT') {
    return 'warning';
  }

  if (deviceStateRaw === 'WARNING') {
    return 'caution';
  }

  if (deviceStateRaw === 'OFF') {
    return 'offline';
  }

  return 'normal';
}

/**
 * SACON 그룹 상태 값을 위젯 상태 값으로 변환합니다.
 */
function mapSaconGroupStateToStatus(value: unknown): DeviceStatusLevel | null {
  return resolveSaconGroupPresentationStatus(value);
}

/**
 * SACON 그룹 집계 값을 위젯 상태 값으로 변환합니다.
 */
function mapSaconGroupDataToStatus(groupData: Record<string, unknown> | null): DeviceStatusLevel | null {
  if (isOfflineGroupData(groupData)) {
    return 'offline';
  }

  return mapSaconGroupStateToStatus(groupData?.state);
}

/**
 * SACON 그룹 집계 값을 기존 장비 원시 상태 타입으로 축약합니다.
 */
function mapSaconGroupDataToDeviceStateRaw(groupData: Record<string, unknown> | null): 'NORMAL' | 'ALERT' | 'WARNING' | 'OFF' | 'INVISUAL' | null {
  const status = mapSaconGroupDataToStatus(groupData);
  if (status === 'warning') {
    return 'ALERT';
  }
  if (status === 'caution') {
    return 'WARNING';
  }
  if (status === 'offline') {
    return 'OFF';
  }
  if (status === 'normal') {
    return 'NORMAL';
  }
  return null;
}

function readLatLonCoordinates(value: unknown): { lat: number; lon: number } | null {
  if (!Array.isArray(value) || value.length < 2) {
    return null;
  }

  const lat = readNumber(value[0]);
  const lon = readNumber(value[1]);
  if (lat !== null && lon !== null && isRenderableLatLon(lat, lon)) {
    return { lat, lon };
  }

  return null;
}

/**
 * 메시지 바디에서 좌표를 추출합니다.
 */
function extractCoordinates(body: Record<string, unknown>): { lat?: number; lon?: number } {
  const groupData = readGroupData(body);
  const groupLatLon = groupData ? readLatLonCoordinates(groupData.lat_lon) : null;
  if (groupLatLon) {
    return groupLatLon;
  }

  const lat = readNumber(body.lat);
  const lon = readNumber(body.lon);

  if (lat !== null && lon !== null && isRenderableLatLon(lat, lon)) {
    return { lat, lon };
  }

  const latLon = readLatLonCoordinates(body.lat_lon);
  if (latLon) {
    return latLon;
  }

  return {};
}

/**
 * 백엔드 장비 이벤트를 위젯 상태 값으로 변환합니다.
 */
function toStatus(message: WsBaseMessage, body: Record<string, unknown>): DeviceStatusLevel {
  const explicitDeviceState = normalizeDeviceStateRaw(body.device_state);
  const isCctv = readBoolean(body.is_cctv) ?? readBoolean(body.isCctv);
  const groupData = readGroupData(body);
  if (isCctv === true && explicitDeviceState && explicitDeviceState !== 'INVISUAL') {
    return mapDeviceStateRawToStatus(explicitDeviceState);
  }

  if (shouldPreferExplicitDeviceState(body, explicitDeviceState)) {
    return 'offline';
  }

  const groupStatus = shouldUseSaconGroupDataState(body, groupData) ? mapSaconGroupDataToStatus(groupData) : null;
  if (groupStatus) {
    return groupStatus;
  }

  if (explicitDeviceState && explicitDeviceState !== 'INVISUAL') {
    return mapDeviceStateRawToStatus(explicitDeviceState);
  }

  if (body.registered === false) {
    return 'unregistered';
  }

  if (message.event === WsEventType.SACON_PRODUCT_WARNING) {
    return 'caution';
  }

  if (message.event === WsEventType.SACON_PRODUCT_ALERT) {
    return 'warning';
  }

  if (message.event === WsEventType.SACON_PRODUCT_INFO || message.event === WsEventType.SACON_PRODUCT_PROCESSED) {
    return 'normal';
  }

  if (body.alarmShouldSend === true || body.alarm_should_send === true) {
    return 'warning';
  }

  return 'normal';
}

/**
 * 개별 장비 메시지를 리스트 아이템 구조로 변환합니다.
 */
export function deriveItemFromMessage(message: WsBaseMessage): DeviceStatusItem | null {
  const body = (message.body ?? {}) as Record<string, unknown>;
  const groupData = readGroupData(body);
  const explicitDeviceState = normalizeDeviceStateRaw(body.device_state);
  const shouldUseGroupDataState = shouldUseSaconGroupDataState(body, groupData);
  const isCctv = readBoolean(body.is_cctv) ?? readBoolean(body.isCctv);
  const cameraIsOn = readBoolean(body.camera_is_on) ?? readBoolean(body.cameraIsOn);
  const cameraStreamHealthy = readBoolean(body.camera_stream_healthy) ?? readBoolean(body.cameraStreamHealthy);
  const cameraEffectiveIsOn = readBoolean(body.camera_effective_is_on) ?? readBoolean(body.cameraEffectiveIsOn);
  const deviceStateRaw =
    isCctv === true && explicitDeviceState
      ? explicitDeviceState
      : shouldPreferExplicitDeviceState(body, explicitDeviceState)
        ? explicitDeviceState
        : ((shouldUseGroupDataState ? mapSaconGroupDataToDeviceStateRaw(groupData) : null) ?? explicitDeviceState);
  const entityUid =
    readString(body.device_uid) ??
    readString(body.deviceUid) ??
    readString(body.display_device_uid) ??
    readString(body.displayDeviceUid) ??
    readString(body.worker_uid) ??
    readString(body.uid) ??
    readString(message.key) ??
    null;

  const id =
    readString(body.device_code) ??
    readString(body.deviceCode) ??
    readString(body.display_device_code) ??
    entityUid ??
    readString(body.device_uid) ??
    readString(body.worker_uid) ??
    readString(body.uid) ??
    readString(message.key) ??
    null;

  if (!id) {
    return null;
  }

  const { lat, lon } = extractCoordinates(body);
  const deviceCode = readString(body.device_code) ?? readString(body.deviceCode) ?? readString(body.display_device_code);
  const productUid = readString(body.product_uid) ?? readString(body.productUid) ?? readString(body.display_product_uid);
  const productName = readString(body.product_name) ?? readString(body.productName) ?? readString(body.product_model_name) ?? readString(body.productModelName);
  const productLoraType = readNumber(body.product_lora_type) ?? readNumber(body.productLoraType) ?? readNumber(body.lora_type) ?? readNumber(body.loraType);
  const productSensorType = readNumber(body.product_sensor_type) ?? readNumber(body.productSensorType) ?? readNumber(body.sensor_type) ?? readNumber(body.sensorType);
  const productSensorTypeName =
    readString(body.product_sensor_type_name) ?? readString(body.productSensorTypeName) ?? readString(body.sensor_type_name) ?? readString(body.sensorTypeName);
  const productSensorTypeLabel =
    readString(body.product_sensor_type_label) ?? readString(body.productSensorTypeLabel) ?? readString(body.sensor_type_label) ?? readString(body.sensorTypeLabel);
  const rId = readString(body.r_id) ?? readString(body.rId) ?? readString(body.display_r_id);
  const sId = readString(body.s_id) ?? readString(body.sId) ?? readString(body.display_s_id);
  const saconState = readStatusCode(body.state) ?? readStatusCode(body.sacon_state);
  const rawData = readString(body.raw_data) ?? readString(body.rawData);
  const gpsValid = readBoolean(body.gps_valid) ?? readBoolean(body.gpsValid);
  const gpsStatus = readStatusCode(body.gps_status) ?? readStatusCode(body.gpsStatus);
  const sensorState = readString(body.sensor_state) ?? readString(body.sensorState);
  const sensorStateName = readString(body.sensor_state_name) ?? readString(body.sensorStateName);
  const powerOperationState = readNumber(body.power_operation_state) ?? readNumber(body.powerOperationState);
  const powerOperationStateName = readString(body.power_operation_state_name) ?? readString(body.powerOperationStateName);
  const powerChargingState = readNumber(body.power_charging_state) ?? readNumber(body.powerChargingState);
  const powerChargingStateName = readString(body.power_charging_state_name) ?? readString(body.powerChargingStateName);
  const routerPower = readNumber(body.router_power) ?? readNumber(body.routerPower);
  const routerTimerControl = readNumber(body.router_timer_control) ?? readNumber(body.routerTimerControl);
  const gpsOnOff = readNumber(body.gps_on_off) ?? readNumber(body.gpsOnOff);
  const gpsTimerControl = readNumber(body.gps_timer_control) ?? readNumber(body.gpsTimerControl);
  const indicatorLed = readNumber(body.indicator_led) ?? readNumber(body.indicatorLed);
  const indicatorTimerControl = readNumber(body.indicator_timer_control) ?? readNumber(body.indicatorTimerControl);
  const powerOffReason = readNumber(body.power_off_reason) ?? readNumber(body.powerOffReason);
  const powerOffReasonName = readString(body.power_off_reason_name) ?? readString(body.powerOffReasonName);
  const connectionState = readStatusCode(body.connection_state);
  const movementEvent = readString(body.movement_event) ?? readString(body.movementEvent);
  const movementEventLabel = readString(body.movement_event_label) ?? readString(body.movementEventLabel);
  const movementOffReason = readString(body.movement_off_reason);
  const movementOffReasonName = readString(body.movement_off_reason_name);
  const movementDisplayLabel = readString(body.movement_display_label);
  const displayDeviceUid = readString(body.display_device_uid) ?? readString(body.displayDeviceUid);
  const displayDeviceName = readString(body.display_device_name) ?? readString(body.displayDeviceName);
  const displayDeviceCode = readString(body.display_device_code) ?? readString(body.displayDeviceCode);
  const displayRId = readString(body.display_r_id) ?? readString(body.displayRId);
  const displaySId = readString(body.display_s_id) ?? readString(body.displaySId);
  const displayProductUid = readString(body.display_product_uid) ?? readString(body.displayProductUid);
  const displayProductSensorType = readNumber(body.display_product_sensor_type) ?? readNumber(body.displayProductSensorType);
  const sourceDeviceUid = readString(body.source_device_uid) ?? readString(body.sourceDeviceUid);
  const sourceDeviceName = readString(body.source_device_name) ?? readString(body.sourceDeviceName);
  const sourceDeviceCode = readString(body.source_device_code) ?? readString(body.sourceDeviceCode);
  const sourceRId = readString(body.source_r_id) ?? readString(body.sourceRId);
  const sourceSId = readString(body.source_s_id) ?? readString(body.sourceSId);
  const sourceProductUid = readString(body.source_product_uid) ?? readString(body.sourceProductUid);
  const sourceProductSensorType = readNumber(body.source_product_sensor_type) ?? readNumber(body.sourceProductSensorType);
  const originalDeviceUid = readString(body.original_device_uid) ?? readString(body.originalDeviceUid);
  const originalDeviceName = readString(body.original_device_name) ?? readString(body.originalDeviceName);
  const originalDeviceCode = readString(body.original_device_code) ?? readString(body.originalDeviceCode);
  const originalRId = readString(body.original_r_id) ?? readString(body.originalRId);
  const originalSId = readString(body.original_s_id) ?? readString(body.originalSId);
  const originalProductUid = readString(body.original_product_uid) ?? readString(body.originalProductUid);
  const originalProductSensorType = readNumber(body.original_product_sensor_type) ?? readNumber(body.originalProductSensorType);
  const workerName = readString(body.worker_name);
  const constructionEquipment = body.construction_equipment ?? body.constructionEquipment;
  const deviceName = readString(body.device_name) ?? readString(body.deviceName);
  const fallbackName = deviceName ?? readString(body.worker_name) ?? readString(body.sensor_state_name) ?? id;
  const name = resolveConstructionEquipmentMarkerName(constructionEquipment, fallbackName);
  const groupDeviceCount = readNumber(body.group_device_count) ?? readNumber(body.groupDeviceCount);
  const groupChildCounterCount = readNumber(body.group_child_counter_count) ?? readNumber(body.groupChildCounterCount);
  const groupTotalDeviceCount = readNumber(body.group_total_device_count) ?? readNumber(body.groupTotalDeviceCount) ?? readNumber(groupData?.device_count);
  const isGroupCounterParent = readBoolean(body.is_group_counter_parent) ?? readBoolean(body.isGroupCounterParent);
  const isGroupCounterChild = readBoolean(body.is_group_counter_child) ?? readBoolean(body.isGroupCounterChild);
  const sourceIsGroupCounterChild = readBoolean(body.source_is_group_counter_child) ?? readBoolean(body.sourceIsGroupCounterChild);
  const cameraUid = readString(body.camera_uid) ?? readString(body.cameraUid);
  const cameraStatus = readStatusCode(body.camera_status) ?? readStatusCode(body.cameraStatus);
  const mainStream = readString(body.main_stream) ?? readString(body.mainStream);
  const subStream = readString(body.sub_stream) ?? readString(body.subStream);
  const hasInstallLocationText = ['location_text', 'locationText', 'install_location_text', 'installLocationText'].some(key => Object.prototype.hasOwnProperty.call(body, key));
  const location = normalizeMarkerInstallLocation(
    readString(body.location_text) ?? readString(body.locationText) ?? readString(body.install_location_text) ?? readString(body.installLocationText)
  );
  const lastSeenAt = normalizeLastSeenTimestamp(
    readString(message.sent_at) ?? readString(body.ingested_at) ?? readString(body.ingestedAt) ?? readString(body.created_at) ?? readString(body.createdAt) ?? undefined
  );
  const deviceUpdatedAt = normalizeLastSeenTimestamp(
    readString(body.device_updated_at) ?? readString(body.deviceUpdatedAt) ?? readString(body.updated_at) ?? readString(body.updatedAt) ?? undefined
  );
  const explicitConstructionEquipmentFlag = readBoolean(body.is_construction_equipment) ?? readBoolean(body.isConstructionEquipment);
  const isConstructionEquipment = explicitConstructionEquipmentFlag ?? (constructionEquipment === undefined || constructionEquipment === null ? undefined : true);

  return {
    id,
    entityUid: entityUid ?? undefined,
    name,
    deviceName: deviceName ?? undefined,
    deviceType: readString(body.device_type) ?? undefined,
    workerName: workerName ?? undefined,
    deviceCode: deviceCode ?? undefined,
    productUid: productUid ?? undefined,
    productName: productName ?? undefined,
    productLoraType: productLoraType === null ? undefined : Math.trunc(productLoraType),
    productSensorType: productSensorType === null ? undefined : Math.trunc(productSensorType),
    productSensorTypeName: productSensorTypeName ?? undefined,
    productSensorTypeLabel: productSensorTypeLabel ?? undefined,
    isCctv: isCctv ?? undefined,
    cameraUid: cameraUid ?? undefined,
    cameraStatus: cameraStatus ?? undefined,
    cameraIsOn: cameraIsOn ?? undefined,
    cameraStreamHealthy: cameraStreamHealthy ?? undefined,
    cameraEffectiveIsOn: cameraEffectiveIsOn ?? undefined,
    mainStream: mainStream ?? undefined,
    subStream: subStream ?? undefined,
    rId: rId ?? undefined,
    sId: sId ?? undefined,
    saconState: saconState ?? undefined,
    rawData: rawData ?? undefined,
    gpsValid: gpsValid ?? undefined,
    gpsStatus: gpsStatus ?? undefined,
    status: toStatus(message, body),
    lastSeen: formatLastSeen(lastSeenAt),
    lastSeenAt,
    location,
    hasInstallLocationText,
    fieldUid: resolveMessageFieldUid(message) ?? undefined,
    lat,
    lon,
    deviceIcon: body.device_icon,
    mapMarkerIcon: resolveConstructionEquipmentMarkerIcon(
      constructionEquipment,
      readDeviceIconConfig(body.map_marker_icon ?? body.mapMarkerIcon),
      readDeviceIconConfig(body.device_icon)
    ),
    constructionEquipment: constructionEquipment ?? undefined,
    isConstructionEquipment,
    isEscapePreventionEnabled: readBoolean(body.is_escape_prevention_enabled) ?? undefined,
    geofenceIsInside: readBoolean(body.geofence_is_inside) ?? undefined,
    escapeAlertActive: readBoolean(body.escape_alert_active) ?? undefined,
    movementStatus: readString(body.movement_status) ?? undefined,
    lastMovementAt: readString(body.last_movement_at) ?? undefined,
    sensorState: sensorState ?? undefined,
    sensorStateName: sensorStateName ?? undefined,
    powerOperationState: powerOperationState === null ? undefined : Math.trunc(powerOperationState),
    powerOperationStateName: powerOperationStateName ?? undefined,
    powerChargingState: powerChargingState === null ? undefined : Math.trunc(powerChargingState),
    powerChargingStateName: powerChargingStateName ?? undefined,
    routerPower: routerPower === null ? undefined : Math.trunc(routerPower),
    routerTimerControl: routerTimerControl === null ? undefined : Math.trunc(routerTimerControl),
    gpsOnOff: gpsOnOff === null ? undefined : Math.trunc(gpsOnOff),
    gpsTimerControl: gpsTimerControl === null ? undefined : Math.trunc(gpsTimerControl),
    indicatorLed: indicatorLed === null ? undefined : Math.trunc(indicatorLed),
    indicatorTimerControl: indicatorTimerControl === null ? undefined : Math.trunc(indicatorTimerControl),
    powerOffReason: powerOffReason === null ? undefined : Math.trunc(powerOffReason),
    powerOffReasonName: powerOffReasonName ?? undefined,
    connectionState: connectionState ?? undefined,
    movementEvent: movementEvent ?? undefined,
    movementEventLabel: movementEventLabel ?? undefined,
    movementOffReason: movementOffReason ?? undefined,
    movementOffReasonName: movementOffReasonName ?? undefined,
    movementDisplayLabel: movementDisplayLabel ?? undefined,
    measurements: readMeasurements(body),
    deviceUpdatedAt,
    batteryPercent: readBatteryPercent(body),
    deviceStateRaw: deviceStateRaw ?? undefined,
    deviceGroupUid: readString(body.device_group_uid) ?? readString(body.deviceGroupUid) ?? undefined,
    mainDeviceUid: readString(body.main_device_uid) ?? readString(body.mainDeviceUid) ?? undefined,
    displayDeviceUid: displayDeviceUid ?? undefined,
    displayDeviceName: displayDeviceName ?? undefined,
    displayDeviceCode: displayDeviceCode ?? undefined,
    displayRId: displayRId ?? undefined,
    displaySId: displaySId ?? undefined,
    displayProductUid: displayProductUid ?? undefined,
    displayProductSensorType: displayProductSensorType === null ? undefined : Math.trunc(displayProductSensorType),
    sourceDeviceUid: sourceDeviceUid ?? undefined,
    sourceDeviceName: sourceDeviceName ?? undefined,
    sourceDeviceCode: sourceDeviceCode ?? undefined,
    sourceRId: sourceRId ?? undefined,
    sourceSId: sourceSId ?? undefined,
    sourceProductUid: sourceProductUid ?? undefined,
    sourceProductSensorType: sourceProductSensorType === null ? undefined : Math.trunc(sourceProductSensorType),
    originalDeviceUid: originalDeviceUid ?? undefined,
    originalDeviceName: originalDeviceName ?? undefined,
    originalDeviceCode: originalDeviceCode ?? undefined,
    originalRId: originalRId ?? undefined,
    originalSId: originalSId ?? undefined,
    originalProductUid: originalProductUid ?? undefined,
    originalProductSensorType: originalProductSensorType === null ? undefined : Math.trunc(originalProductSensorType),
    groupName: readString(body.group_name) ?? undefined,
    groupDeviceCount: groupDeviceCount === null ? undefined : Math.max(0, Math.trunc(groupDeviceCount)),
    groupChildCounterCount: groupChildCounterCount === null ? undefined : Math.max(0, Math.trunc(groupChildCounterCount)),
    groupTotalDeviceCount: groupTotalDeviceCount === null || groupTotalDeviceCount <= 0 ? undefined : Math.max(1, Math.trunc(groupTotalDeviceCount)),
    isGroupMainDevice: readBoolean(body.is_group_main_device) ?? undefined,
    isGroupCounterParent: isGroupCounterParent ?? undefined,
    isGroupCounterChild: isGroupCounterChild ?? undefined,
    sourceIsGroupCounterChild: sourceIsGroupCounterChild ?? undefined,
    usesGroupData: groupData !== null,
  };
}

/**
 * DEVICE_COUNT 메시지를 카드 집계 구조로 변환합니다.
 */
export function mapDeviceCountMessageToSummary(message: WsDeviceCountMessage): DeviceStatusSummary {
  const workerActive = message.body.counts.filter(item => item.device_type === 'WORKER' || item.device_type === 'MOBILE').reduce((sum, item) => sum + item.active_count, 0);

  return {
    field: 0, // 백엔드 메시지에서 보충 필요하거나 별도 로직 필요
    device: message.body.active_total,
    worker: workerActive,
    alarm: 0, // 별도 집계 필요
  };
}

/**
 * 리스트 아이템을 기준으로 요약 집계를 재계산합니다.
 */
export function recomputeSummary(previous: DeviceStatusSummary, items: DeviceStatusItem[]): DeviceStatusSummary {
  const byStatus: Record<DeviceStatusLevel, number> = {
    normal: 0,
    warning: 0,
    caution: 0,
    offline: 0,
    unregistered: 0,
    unknown: 0,
  };

  for (const item of items) {
    byStatus[item.status] += 1;
  }

  return {
    ...previous,
    device: items.length,
    alarm: byStatus.warning + byStatus.caution,
  };
}

/**
 * 새 메시지를 현재 스냅샷에 반영한 결과를 반환합니다.
 */
export function applyDeviceStatusMessage(snapshot: DeviceStatusSnapshot, message: WsBaseMessage): DeviceStatusSnapshot {
  if (message.event === WsEventType.DEVICE_COUNT) {
    return {
      ...snapshot,
      source: 'sse',
      summary: mapDeviceCountMessageToSummary(message as WsDeviceCountMessage),
      updatedAt: nowIso(),
    };
  }

  const item = deriveItemFromMessage(message);
  if (!item) {
    return snapshot;
  }

  if (item.deviceStateRaw === 'INVISUAL') {
    const items = snapshot.items.filter(existingItem => existingItem.id !== item.id);

    return {
      ...snapshot,
      source: 'sse',
      items,
      summary: recomputeSummary(snapshot.summary, items),
      updatedAt: nowIso(),
    };
  }

  const dedupedItems = snapshot.items.filter(existingItem => existingItem.id !== item.id);
  const items = [item, ...dedupedItems].slice(0, 40);

  return {
    ...snapshot,
    source: 'sse',
    items,
    summary: recomputeSummary(snapshot.summary, items),
    updatedAt: nowIso(),
  };
}

/**
 * SSE 페이로드가 장비 현황 스냅샷인지 최소 형태를 검증합니다.
 */
export function isDeviceStatusSnapshot(value: unknown): value is DeviceStatusSnapshot {
  return deviceStatusSnapshotSchema.safeParse(value).success;
}
